"use client"

import { removeJwtCookies } from "../../util/cookies"
import { retryWithBackoff } from "../../util/retry"
import { usePrivy } from "@privy-io/react-auth"
import { redirect, useSearchParams } from "next/navigation"
import { useEffect } from "react"
import FullPageLoading from "../components/Loading"

/** This page is used to try to refresh the Privy token client-side */
export default function RefreshPage({
  searchParams,
}: {
  searchParams: Promise<{ redirect?: string }>
}) {
  const { getAccessToken, logout, ready } = usePrivy()
  const params = useSearchParams()

  useEffect(() => {
    // Step 1: Wait for Privy provider to be ready.
    console.log("[/refresh] Checking if Privy provider is ready...", {
      ready,
    })

    if (!ready) return

    const refreshToken = async () => {
      const redirectUri = params?.get("redirect") || "/home"

      console.log(
        "[/refresh] Starting token refresh process, redirecting to:",
        redirectUri
      )

      // Step 2: Retry getting access token with exponential backoff.
      const tokenResult = await retryWithBackoff(
        async () => {
          const token = await getAccessToken()
          if (!token) {
            throw new Error("No token received from Privy")
          }
          return token
        },
        {
          maxAttempts: 3,
          baseDelay: 1000,
          maxDelay: 4000,
          fnName: "getAccessToken",
        }
      )

      if (tokenResult.success) {
        console.log("[/refresh] Token refresh successful, redirecting to:", {
          redirectUri,
          ready,
        })

        // If redirectUri is present, redirect there. Otherwise go back to the previous page.
        if (redirectUri) {
          redirect(redirectUri)
        } else {
          window.history.back()
        }
        return
      }

      // All attempts failed - logout and redirect to login.
      console.error(
        "[/refresh] Token refresh failed after all attempts. Logging out and redirecting to login.",
        { ready }
      )

      try {
        removeJwtCookies()
        logout()
      } catch (err) {
        console.error("[/refresh] Error during logout:", { err, ready })
      }

      window.location.replace(
        `/?login=true&redirect=${encodeURIComponent(redirectUri)}`
      )
    }

    refreshToken()
  }, [ready])

  return <FullPageLoading />
}
